import gql from 'graphql-tag';
import { CREDIT_LEDGER_ENTRY_FRAGMENT, PLAN_TERM_STATE_FRAGMENT } from '../superadmin';

const ORGANISATION_FRAGMENT = gql`
  fragment Organisations on Organisation {
    id
    name
    usersAmount
    myPermissions
    stats {
      totalPointCount
      totalUsers
      totalProjects
    }
    isRootOrganisation
    subscription {
      type
      scheduledType
      scheduledAt
      paused
      pausedAt
      startDate
      isFlex
      endDate
      projectUnitPrice
      subscriptionPrice
      cancellable
      minPurchaseAmount
      maxPurchaseAmount
      canAddSimultaneousUploads
      simultaneousUploadsLeft
    }
    prohibitedActions
    referral {
      managedBy {
        organisationId
        name
      }
      billedTo {
        organisationId
        name
      }
      canCancelSubscription
    }
    credits {
      balance
      pending
      available
      nextExpiry
      monthlyAllowance {
        total
        remaining
        renewsAt
      }
      liveGrants {
        id
        source
        remaining
        expiresAt
      }
    }
    createdAt
  }
`;

export const ORGANISATIONS = gql`
  ${ORGANISATION_FRAGMENT}
  query organisations($includeIds: [ID!]) {
    organisations(includeIds: $includeIds) {
      ...Organisations
    }
  }
`;

export const ORGANISATION_UPDATE = gql`
  mutation organisationUpdate($organisationId: ID!, $name: String!) {
    organisationUpdate(organisationId: $organisationId, name: $name) {
      id
      name
    }
  }
`;

export const ORGANISATIONS_CREATE = gql`
  ${ORGANISATION_FRAGMENT}
  mutation organisationCreate($name: String!) {
    organisationCreate(name: $name) {
      ...Organisations
    }
  }
`;

export const ORGANISATION_DELETE = gql`
  mutation organisationDelete($organisationId: ID!) {
    organisationDelete(organisationId: $organisationId)
  }
`;

export const ORGANISATION_CREDIT_LEDGER = gql`
  ${CREDIT_LEDGER_ENTRY_FRAGMENT}
  query organisationCreditLedger($organisationId: ID!, $pagination: CursorPaginationInput) {
    organisationCreditLedger(organisationId: $organisationId, pagination: $pagination) {
      data {
        ...CreditLedgerEntryFields
        # Asks Odoo (once for the page): only the histories select it, not every mutation that returns a row.
        sentInvoiceNumber
        paymentStatus
      }
      nextCursor
      total
      hasMore
    }
  }
`;

export const ORGANISATION_INVOICE_PDF = gql`
  query organisationInvoicePdf($organisationId: ID!, $entryId: ID!) {
    organisationInvoicePdf(organisationId: $organisationId, entryId: $entryId) {
      filename
      base64
    }
  }
`;

export const ORGANISATION_CANCEL_SUBSCRIPTION = gql`
  ${ORGANISATION_FRAGMENT}
  mutation organisationCancelSubscription($organisationId: ID!) {
    organisationCancelSubscription(organisationId: $organisationId) {
      ...Organisations
    }
  }
`;

export const ORGANISATION_CONTACT_SALES = gql`
  mutation organisationContactSales($organisationId: ID!, $message: String!) {
    organisationContactSales(organisationId: $organisationId, message: $message)
  }
`;

const ORGANISATION_BILLING_FRAGMENT = gql`
  fragment OrganisationBillingFields on OrganisationBilling {
    id
    enabled
    linked
    complete
    blank
    info {
      companyName
      street
      street2
      city
      zip
      countryCode
      vat
      email
    }
  }
`;

export const ORGANISATION_BILLING = gql`
  ${ORGANISATION_BILLING_FRAGMENT}
  query organisationBilling($organisationId: ID!) {
    organisationBilling(organisationId: $organisationId) {
      ...OrganisationBillingFields
    }
  }
`;

// For the superadmin's subscription modal: also the organisation's Odoo link as the lookup left it (finding the
// customer can change it), so the cached organisation — and the link shown from it — never lags behind the details.
export const ORGANISATION_BILLING_WITH_ODOO = gql`
  ${ORGANISATION_BILLING_FRAGMENT}
  query organisationBillingWithOdoo($organisationId: ID!) {
    organisationBilling(organisationId: $organisationId) {
      ...OrganisationBillingFields
      # The organisation's Odoo link as the lookup left it — the subscription's reference included, filled in by
      # the lookup where it was missing — merged into the card's organisation in the cache.
      organisation {
        id
        odoo {
          leadId
          partnerId
          leadUrl
          partnerUrl
          orderId
          orderName
        }
      }
    }
  }
`;

export const ORGANISATION_LINK_BILLING = gql`
  ${ORGANISATION_BILLING_FRAGMENT}
  mutation organisationLinkBilling($organisationId: ID!) {
    organisationLinkBilling(organisationId: $organisationId) {
      ...OrganisationBillingFields
    }
  }
`;

// The same link for the superadmin's "auto link", answering with the organisation's Odoo link as well: the
// details and the link shown with them then update together, from the one answer.
export const ORGANISATION_LINK_BILLING_WITH_ODOO = gql`
  ${ORGANISATION_BILLING_FRAGMENT}
  mutation organisationLinkBillingWithOdoo($organisationId: ID!) {
    organisationLinkBilling(organisationId: $organisationId) {
      ...OrganisationBillingFields
      organisation {
        id
        odoo {
          leadId
          partnerId
          leadUrl
          partnerUrl
        }
      }
    }
  }
`;

export const ORGANISATION_UPDATE_BILLING = gql`
  ${ORGANISATION_BILLING_FRAGMENT}
  mutation organisationUpdateBilling($organisationId: ID!, $info: InputBillingInfo!) {
    organisationUpdateBilling(organisationId: $organisationId, info: $info) {
      ...OrganisationBillingFields
    }
  }
`;

export const ORGANISATION_PURCHASE_CREDITS = gql`
  ${CREDIT_LEDGER_ENTRY_FRAGMENT}
  mutation organisationPurchaseCredits($organisationId: ID!, $amount: Int!) {
    organisationPurchaseCredits(organisationId: $organisationId, amount: $amount) {
      ...CreditLedgerEntryFields
    }
  }
`;

// The customer's own plan change and its simulation: the superadmin's rule with the customer's defaults.
export const ORGANISATION_PLAN_CHANGE_PREVIEW = gql`
  ${PLAN_TERM_STATE_FRAGMENT}
  query organisationPlanChangePreview($organisationId: ID!, $subscriptionType: SubscriptionType!) {
    organisationPlanChangePreview(organisationId: $organisationId, subscriptionType: $subscriptionType) {
      kind
      subscriptionType
      effectiveAt
      scheduled
      endDate
      current {
        ...PlanTermStateFields
      }
      after {
        ...PlanTermStateFields
      }
      next {
        ...PlanTermStateFields
      }
      lines {
        subscriptionType
        from
        to
        months
        monthlyPriceCents
        totalCents
      }
      totalValueCents
      currentTermValueCents
      dueCents
      chargedCents
      priceLocked
      extraCredits
      extraCreditsAt
      extraCreditsExpireAt
      problems
    }
  }
`;

export const ORGANISATION_CHANGE_PLAN = gql`
  ${ORGANISATION_FRAGMENT}
  mutation organisationChangePlan($organisationId: ID!, $subscriptionType: SubscriptionType!) {
    organisationChangePlan(organisationId: $organisationId, subscriptionType: $subscriptionType) {
      ...Organisations
    }
  }
`;

// Next to the fragment it uses: the superadmin documents importing it from here made an import cycle.
export const SUPERADMIN_SET_BILLING_BLANK = gql`
  ${ORGANISATION_BILLING_FRAGMENT}
  mutation superadminSetBillingBlank($organisationId: ID!, $blank: Boolean!) {
    superadminSetBillingBlank(organisationId: $organisationId, blank: $blank) {
      ...OrganisationBillingFields
    }
  }
`;
